import { z } from "zod";

import { GetReviewCycleLogsFrontendResponse } from "@performyard/service-contracts";

import request from "./internal-base-request";

const GetReviewCycleLogsParamsSchema = z.object({
	resourceTypes: z.array(z.string()).optional(),
	resourceIds: z.array(z.string()).optional(),
	changeTypes: z.array(z.string()).optional(),
	subjectIds: z.array(z.string()).nullable().optional(),
	limit: z.number().optional(),
	skip: z.number().optional(),
	sort: z
		.object({
			name: z.string(),
			direction: z.enum(["asc", "desc"]),
		})
		.optional(),
});

export type GetReviewCycleLogsParams = z.infer<typeof GetReviewCycleLogsParamsSchema>;

// Column names need to be mapped to search service property names
const sortMap: Record<string, string> = {
	assignedDate: "changed_at",
	subject: "subject_name",
};

/**
 * Retrieves review cycle logs from the resource log collection using the internal-api search service for use in the assignment log page.
 *
 * @param {GetReviewCycleLogsParams} params - The parameters for filtering and sorting the logs.
 * @returns {Promise<GetReviewCycleLogsFrontendResponse>} A promise that resolves to the frontend response containing review cycle logs.
 */
export const getReviewCycleLogs = async (
	params: GetReviewCycleLogsParams
): Promise<GetReviewCycleLogsFrontendResponse> => {
	const validatedParams = GetReviewCycleLogsParamsSchema.parse(params);
	const baseUrl = "/v1/search";
	const searchParams = new URLSearchParams();

	searchParams.append("service", "review_cycle_log");

	// Function to append each array parameter to the query
	const appendArrayParam = (key: string, values?: string[] | null) => {
		values?.forEach((value) => searchParams.append(`${key}_in`, value));
	};

	appendArrayParam("resource_type", validatedParams.resourceTypes);
	appendArrayParam("resource_id", validatedParams.resourceIds);
	appendArrayParam("change_type", validatedParams.changeTypes);
	appendArrayParam("subject_id", validatedParams.subjectIds);

	// If skip and/or limit provided, append to query
	if (validatedParams.limit !== undefined) searchParams.append("limit", validatedParams.limit.toString());
	if (validatedParams.skip !== undefined) searchParams.append("skip", validatedParams.skip.toString());

	// If custom sort provided, map sort parameter and append to query
	// Otherwise, default to assigned date descending
	if (validatedParams.sort) {
		const { name, direction } = validatedParams.sort;
		const sortName = sortMap[name] || name;
		searchParams.append("sort", `${direction}(${sortName})`);
	} else {
		const defaultSort = "desc(assignedDate)";
		searchParams.append("sort", defaultSort);
	}

	const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";
	const [resourceLogsResponse] = await request<GetReviewCycleLogsFrontendResponse>(`${baseUrl}${queryString}`);

	return {
		...resourceLogsResponse,
		results: resourceLogsResponse.results.map((resourceLog) => ({
			...resourceLog,
			changedAt: new Date(resourceLog.changedAt),
		})),
	};
};
